import { Box, Heading, Image, Text } from 'theme-ui';
import AspectRatio from 'components/ui/AspectRatio';
import { Link } from 'components/ui/links';
import { getCloudinaryFetchUrl } from 'lib/cloudinary';
import { getSrcSet } from 'lib/contentful-helpers';

type Props = {
  title: string;
  subtitle: string;
  subtitleUrl?: string;
  heroImageUrl: string;
  heroImageAlt: string;
  date?: string;
  authorName?: string;
  authorUrl?: string;
  authorVerb?: string;
};

export default function PageHero({
  title,
  subtitle,
  subtitleUrl,
  heroImageUrl,
  heroImageAlt,
  date,
  authorUrl,
  authorName,
  authorVerb = 'Written',
}: Props) {
  const IMG_MAX_WIDTH = 1266;
  const MOBILE_BREAKPOINT = 768;
  const MOBILE_ASPECT_RATIO = 375 / 233;
  const DESKTOP_ASPECT_RATIO = 1200 / 468;

  const DEFAULT_IMG_URL =
    'https://images.ctfassets.net/mbrzsuirfz03/uKjVc0YqY1ByaO9eZMxDg/7ddce43ebc5d0f3e49451f7bdca59ccc/iphone.png';

  const url = `${heroImageUrl || DEFAULT_IMG_URL}`;

  return (
    <Box variant="grids.full" sx={{ mb: [24, 64] }}>
      <Box
        variant="grids.item"
        sx={{
          gridColumn: ['1 / 13', '1 / 10', '1 / 7'],
          gridRow: '1 / 2',
          alignSelf: ['start', 'end'],
          zIndex: 'topContent',
        }}
      >
        {subtitleUrl ? (
          <Link
            href={subtitleUrl}
            sx={{ ':hover': { textDecoration: 'none' } }}
          >
            <Subtitle>{subtitle}</Subtitle>
          </Link>
        ) : (
          <Subtitle>{subtitle}</Subtitle>
        )}
        <Heading as="h1" variant="displayMedium" sx={{ color: 'white' }}>
          {title}
        </Heading>
        {date && authorName && (
          <AuthorAndDate
            date={date}
            authorName={authorName}
            authorUrl={authorUrl}
            authorVerb={authorVerb}
          />
        )}
      </Box>
      <AspectRatio
        ratio={[MOBILE_ASPECT_RATIO, DESKTOP_ASPECT_RATIO]}
        sx={{
          gridColumn: '1 / 13',
          gridRow: '1 / 2',
        }}
      >
        <picture>
          <source
            media={`(max-width: ${MOBILE_BREAKPOINT}px)`}
            sizes="100vw"
            srcSet={getSrcSet(
              url,
              [375, 375 * 2, 375 * 3],
              MOBILE_ASPECT_RATIO,
              { gravity: 'west' }
            )}
          />
          <source
            media={`(min-width: ${MOBILE_BREAKPOINT + 1}px)`}
            sizes={`${IMG_MAX_WIDTH}px`}
            srcSet={getSrcSet(
              url,
              [IMG_MAX_WIDTH, IMG_MAX_WIDTH * 2],
              DESKTOP_ASPECT_RATIO
            )}
          />
          <Image
            src={getCloudinaryFetchUrl(url, {})}
            alt={heroImageAlt}
            loading="eager"
            sx={{
              width: '100%',
              height: 'auto',
              objectFit: 'cover',
              borderRadius: 'large',
            }}
          />
        </picture>
      </AspectRatio>
    </Box>
  );
}

function Subtitle({ children }: { children: React.ReactNode }) {
  return (
    <Heading
      as="h2"
      variant="titleEmphasis"
      sx={{ mb: [12, 21], color: 'white' }}
    >
      {children}
    </Heading>
  );
}

function AuthorAndDate({
  date,
  authorUrl,
  authorName,
  authorVerb,
}: {
  date: string;
  authorUrl: string;
  authorName: string;
  authorVerb: string;
}) {
  return (
    <Box as="section" sx={{ color: 'sWhite', mt: [12, 26] }}>
      <Text sx={{ color: 'sWhite' }}>
        {authorVerb} by{' '}
        <Box as="address" sx={{ display: 'inline' }}>
          <Link
            href={authorUrl}
            rel="author"
            sx={{ color: 'sWhite', fontWeight: 'medium' }}
          >
            {authorName}
          </Link>
        </Box>{' '}
        on{' '}
        <Box as="span" sx={{ fontWeight: 'medium' }}>
          <time dateTime={date}>{formatDate(date)}</time>
        </Box>
      </Text>
    </Box>
  );
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}
